import { useNavigate } from 'react-router-dom'
import { Box, Button, CircularProgress } from '@mui/material'
import { FC, useCallback } from 'react'

import { Alert } from '../../common/Alert'
import AttorneyDropdown from '../../common/AttorneyDropdown'
import MatterDropdown from '../../common/MatterDropdown'
import {
  CaseWithLowTrustBalancePayload,
  useCreateFundRequestMutation,
  CreateFundRequestInput
} from '../../../../generated'
import { QUERY_PARAMS } from '../../../constants'
import { flexCenter, flexJustifySpaceBetween } from '../../../theme/styleConstants'
import { getGraphQLMutationErrorMessage } from '../../../utils/billing'
import { GenerateFundRequestFilters } from '../../../interfaceTypes/props'

interface GenerateFundRequestsHeaderProps {
  selectedCases: string[]
  cases: CaseWithLowTrustBalancePayload[] | null
  filters: GenerateFundRequestFilters
  handleFiltersChange: (filters: GenerateFundRequestFilters) => void
}

const GenerateFundRequestsHeader: FC<GenerateFundRequestsHeaderProps> = ({
  selectedCases,
  cases,
  filters,
  handleFiltersChange
}) => {
  const navigate = useNavigate()

  const [createFundRequest, { loading: generatingFundRequests }] = useCreateFundRequestMutation({
    onError(error) {
      Alert.error(getGraphQLMutationErrorMessage(error))
    },
    onCompleted(data) {
      if (data) {
        const {
          createFundRequest: { response, fundRequests }
        } = data

        if (response?.status === 201 && fundRequests.length > 0) {
          Alert.success('Fund requests generated successfully')
          navigate('/billing/fund-requests')
        }
      }
    }
  })

  const prepareFundRequestsData = (): CreateFundRequestInput[] => {
    if (!cases || selectedCases.length === 0) return []

    const selectedCaseData = cases.filter(caseItem => selectedCases.includes(caseItem.id))

    return selectedCaseData.map(caseItem => ({
      caseId: caseItem.id,
      amount: caseItem.amountToBill,
      description: ''
    }))
  }

  const handleCreateFundRequests = async () => {
    if (selectedCases.length === 0) {
      Alert.error('Please select at least one case to generate fund requests')
      return
    }

    const data = prepareFundRequestsData()
    await createFundRequest({
      variables: {
        createFundRequestInput: data
      }
    })
  }

  const getFilterValues = useCallback(
    (key: QUERY_PARAMS) => (key === QUERY_PARAMS.MATTER ? (filters.case?.id ?? QUERY_PARAMS.ALL) : ''),
    [filters.case?.id]
  )

  const setParams = useCallback(
    (key: QUERY_PARAMS, value: string) => {
      if (key === QUERY_PARAMS.MATTER) {
        handleFiltersChange({
          ...filters,
          case: value === QUERY_PARAMS.ALL ? undefined : { id: value, title: undefined }
        })
      }
    },
    [filters, handleFiltersChange]
  )

  const customOnChange = useCallback(
    (option: { id: string; title?: string | null } | null) => {
      handleFiltersChange({
        ...filters,
        case: option ? { id: option.id, title: option.title ?? undefined } : undefined
      })
    },
    [filters, handleFiltersChange]
  )

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: '1px solid #e0e0e0',
        gap: 1,
        flexWrap: 'wrap',
        ...flexCenter,
        ...flexJustifySpaceBetween
      }}
    >
      <Box sx={{ display: 'flex', gap: 1 }}>
        <AttorneyDropdown
          selectedAttorneyId={filters.attorneyId || undefined}
          label="Select Attorney"
          sx={{ minWidth: 200 }}
          onChange={value => handleFiltersChange({ ...filters, attorneyId: value?.id })}
        />
        <MatterDropdown
          label="Select Matter"
          sx={{ minWidth: 320 }}
          setParams={setParams}
          getFilterValues={getFilterValues}
          customOnChange={customOnChange}
        />
      </Box>
      <Button
        variant="contained"
        onClick={handleCreateFundRequests}
        disabled={selectedCases.length === 0 || generatingFundRequests}
        endIcon={generatingFundRequests ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        GENERATE {selectedCases.length > 0 && `(${selectedCases.length})`}
      </Button>
    </Box>
  )
}

export default GenerateFundRequestsHeader
